import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Key, Trash2 } from 'lucide-react';
import { Modal, Button, Input, Select } from '../ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface PixKeyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  currentPixKey?: string | null;
  currentPixKeyType?: string | null;
  onSuccess: () => void;
}

const PIX_KEY_TYPES = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'PHONE', label: 'Telefone' },
  { value: 'RANDOM', label: 'Chave Aleatória' },
];

function applyMask(value: string, type: string): string {
  const digits = value.replace(/\D/g, '');
  switch (type) {
    case 'CPF':
      return digits
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    case 'CNPJ':
      return digits
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    case 'PHONE':
      if (!value.startsWith('+')) {
        const d = digits.slice(0, 13);
        if (d.length <= 2) return `+${d}`;
        if (d.length <= 4) return `+${d.slice(0, 2)} ${d.slice(2)}`;
        return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}${d.length > 9 ? '-' + d.slice(9) : ''}`;
      }
      return value;
    default:
      return value;
  }
}

function unmask(value: string, type: string): string {
  switch (type) {
    case 'CPF':
    case 'CNPJ':
      return value.replace(/\D/g, '');
    case 'PHONE':
      return value.startsWith('+') ? '+' + value.replace(/\D/g, '') : '+55' + value.replace(/\D/g, '');
    default:
      return value;
  }
}

export function PixKeyFormModal({
  isOpen,
  onClose,
  accountId,
  currentPixKey,
  currentPixKeyType,
  onSuccess,
}: PixKeyFormModalProps) {
  const queryClient = useQueryClient();
  const [pixKeyType, setPixKeyType] = useState(currentPixKeyType || 'CPF');
  const [pixKeyDisplay, setPixKeyDisplay] = useState(currentPixKey || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPixKeyType(currentPixKeyType || 'CPF');
      setPixKeyDisplay(currentPixKey || '');
      setError('');
    }
  }, [isOpen, currentPixKey, currentPixKeyType]);

  const updateMutation = useMutation({
    mutationFn: (data: { pixKey: string | null; pixKeyType: string | null }) =>
      api.put(ENDPOINTS.virtualAccounts.updatePixKey(accountId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account-stats'] });
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Erro ao atualizar chave PIX');
    },
  });

  const removeMutation = useMutation({
    mutationFn: () =>
      api.put(ENDPOINTS.virtualAccounts.updatePixKey(accountId), { pixKey: null, pixKeyType: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account-stats'] });
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Erro ao remover chave PIX');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const rawKey = unmask(pixKeyDisplay, pixKeyType);
    updateMutation.mutate({ pixKey: rawKey, pixKeyType });
  };

  const handleInputChange = (value: string) => {
    if (['CPF', 'CNPJ', 'PHONE'].includes(pixKeyType)) {
      setPixKeyDisplay(applyMask(value, pixKeyType));
    } else {
      setPixKeyDisplay(value);
    }
  };

  const handleTypeChange = (newType: string) => {
    setPixKeyType(newType);
    setPixKeyDisplay('');
    setError('');
  };

  const getPlaceholder = () => {
    switch (pixKeyType) {
      case 'CPF': return '000.000.000-00';
      case 'CNPJ': return '00.000.000/0000-00';
      case 'EMAIL': return 'email@exemplo.com';
      case 'PHONE': return '+55 11 99999-9999';
      case 'RANDOM': return 'Cole sua chave aleatória';
      default: return '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={currentPixKey ? 'Atualizar Chave PIX' : 'Cadastrar Chave PIX'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Tipo de Chave PIX"
          value={pixKeyType}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {PIX_KEY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>

        <Input
          label="Chave PIX"
          value={pixKeyDisplay}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={getPlaceholder()}
          required
        />

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-4">
          <div>
            {currentPixKey && (
              <Button
                type="button"
                variant="ghost"
                className="text-red-400 hover:text-red-300"
                onClick={() => removeMutation.mutate()}
                disabled={removeMutation.isPending}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {removeMutation.isPending ? 'Removendo...' : 'Remover Chave'}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={updateMutation.isPending || !pixKeyDisplay}>
              <Key className="w-4 h-4 mr-2" />
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
